import React from 'react';
import { List } from 'immutable';
import classNames from 'classnames';
import { Paragraph, View, Block } from '@uncinc/react-drupal-core';
import { useLastLocation } from 'react-router-last-location';
import Helmet from 'react-helmet';

import { useAspectRatio } from '../../../../helpers/useAspectRatio';
import i18next from '../../../../config/i18next';

import {
  Container,
  WysiwygField,
  PatternBlock,
  Share,
  Divider,
  Breadcrumbs,
  DynamicMediaItem,
} from '../../../../components';

import { formatDate } from '../../../../helpers/dates';
import {
  SOCIAL_CTA_BLOCK_ID,
  WANT_TO_KNOW_MORE_CTA_BLOCK_ID,
} from '../../../../config/constants';

import './style.scss';

const SIAProjectFull = ({ id, entity, className }) => {
  const parentProject = entity.getIn(['field_parent_project', 0, 'entity']);
  const companyName = entity.getIn(['field_company_name', 0, 'value']);
  const backgroundImage = entity.getIn(['field_image', 0], null);
  const ogImage = entity.getIn(['field_image', 0, 'styles', 'large']);
  const targetAudiences = entity.getIn(['field_target_audience']);
  const parentUrl = entity.getIn(['field_parent_project', 0, 'url']);

  const title = entity.getIn(['title', 0, 'value'], parentProject.getIn(['title', 0, 'value']));
  const locationFormatted = parentProject.getIn(['field_address', 0, 'locality']);
  const amount = parentProject.getIn(['field_amount', 0, 'value'], '-');

  const classes = classNames(className);
  const previousLocation = useLastLocation();
  let previousUrl;

  if (previousLocation && previousLocation.pathname === '/sociaal-initiatiefnemers' && previousLocation.search) {
    previousUrl = previousLocation.pathname + previousLocation.search;
  } else {
    previousUrl = '/sociaal-initiatiefnemers';
  }

  const aspectRatio = useAspectRatio({
    small: '1_1',
    medium: '2_1',
    large: '7_3',
    xlarge: '8_3',
  });

  return (
    <div className="SIAProject">
      <Helmet>
        <meta property="og:image" content={ogImage} />
      </Helmet>
      <Container>
        <Breadcrumbs includeCurrent />
      </Container>

      <div className="SIAProject__hero">
        <DynamicMediaItem
          className="SIAProject__hero__background"
          media={backgroundImage.toJS()}
          imageAspectRatio={aspectRatio}
          alt={backgroundImage.toJS().alt}
        />
      </div>

      <Container medium>
        <PatternBlock className="SIAProject__introduction-block">
          <h1>{title}</h1>
          <WysiwygField
            property="schema:text"
            className="node--project--body"
            html={entity.getIn(['body', 0, 'processed'], '')}
          />
        </PatternBlock>
        <article typeof="schema:WebPage" className={classes}>
          <div className="row">
            <div className="col-xs-12 col-md-5 offset-md-1">
              <div>
                <div className="SIAProject__info__title">Naam van organisatie:</div>
                <div className="SIAProject__info__info">{companyName || '-'}</div>
              </div>
              <div>
                <div className="SIAProject__info__title">Jaar van toekenning:</div>
                <div className="SIAProject__info__info">{formatDate(entity.getIn(['field_date', 0, 'value']), 'yyyy') || '-'}</div>
              </div>
              <div>
                <div className="SIAProject__info__title">Locatie:</div>
                <div className="SIAProject__info__info SIAProject__info__info--last">{locationFormatted || '-'}</div>
              </div>
            </div>
            <div className="col-xs-12 col-md-5">
              <div>
                <div className="SIAProject__info__title">Doelgroepen:</div>
                <div className="SIAProject__info__info">
                  {targetAudiences.map((item, index) => {
                    const targetAudiencesSize = targetAudiences.size;

                    const targetAudienceTitle = item.getIn(['entity', 'name', 0, 'value'], '');
                    return <div className="SIAProject__info__info__item" key={targetAudienceTitle}>{`${targetAudienceTitle}${targetAudiencesSize !== 1 ? ', ' : ''}`}</div>;
                  })}
                </div>
              </div>
              <div>
                <div className="SIAProject__info__title">Toegekend bedrag:</div>
                <div className="SIAProject__info__info SIAProject__info__info--last">{amount}</div>
              </div>
            </div>
            <div className="col-xs-12 col-md-10 offset-md-1">
              <Divider />
            </div>
            <div className="col-xs-12 col-md-10 offset-md-1">
              <div className="Paragraphs">
                {entity.getIn(['field_paragraphs'], List()).map((item, index) => {
                  const paragraphId = item.get('target_id');
                  const paragraph = item.getIn(['entity']);
                  return (
                    <Paragraph key={paragraphId} entity={paragraph} id={paragraphId} containerless />
                  );
                })}
              </div>
              <Block overrideUrl={parentUrl} buttonPrimary id={WANT_TO_KNOW_MORE_CTA_BLOCK_ID} />
              <Block overrideUrl={previousUrl} id={SOCIAL_CTA_BLOCK_ID} />
              <Share title={i18next.t('project_share_business')} pageTitle={entity.getIn(['title', 0, 'value'])} />
            </div>
          </div>
        </article>

      </Container>
      <View id="related_sia_projects" display="block_1" args={[id]} />
    </div>
  );
};

export default SIAProjectFull;
